import apiClient from '../http/apiClient';

const WORKFLOW_BASE = '/workspaces/workflows';

/**
 * The flat `/workflow-runs/{id}/…` routes carry no workspace in their path, and
 * `IsOrgOwnerOrMember` refuses a request it cannot scope: *"Organization
 * identifier is required for this action."* The runs LIST works because it is
 * nested under a workflow; every per-run control was 403ing because it is not.
 *
 * The permission resolver reads `workspace_id` from view kwargs, body, or query
 * params, so a query param serves GET and POST alike and keeps one shape.
 */
const withWorkspace = (
  workspaceId: string | null | undefined,
  params?: Record<string, unknown>
) => (workspaceId ? { ...(params || {}), workspace_id: workspaceId } : params);

export const workflowApi = {
  fetchTemplates: (params?: Record<string, unknown>) =>
    apiClient.get(
      `${WORKFLOW_BASE}/workflow-templates/`,
      params ? { params } : undefined
    ),

  fetchTemplate: (templateId: string) =>
    apiClient.get(`${WORKFLOW_BASE}/workflow-templates/${templateId}/`),

  createTemplate: (payload: Record<string, unknown>) =>
    apiClient.post(`${WORKFLOW_BASE}/workflow-templates/`, payload),

  fetchWorkflows: (params?: Record<string, unknown>) =>
    apiClient.get(
      `${WORKFLOW_BASE}/workflows/`,
      params ? { params } : undefined
    ),

  fetchWorkflow: (workflowId: string) =>
    apiClient.get(`${WORKFLOW_BASE}/workflows/${workflowId}/`),

  createWorkflow: (payload: Record<string, unknown>) =>
    apiClient.post(`${WORKFLOW_BASE}/workflows/`, payload),

  updateWorkflow: (workflowId: string, payload: Record<string, unknown>) =>
    apiClient.patch(`${WORKFLOW_BASE}/workflows/${workflowId}/`, payload),

  publishWorkflow: (workflowId: string, payload: Record<string, unknown>) =>
    apiClient.post(
      `${WORKFLOW_BASE}/workflows/${workflowId}/publish/`,
      payload
    ),

  archiveWorkflow: (workflowId: string) =>
    apiClient.post(`${WORKFLOW_BASE}/workflows/${workflowId}/archive/`),

  cloneWorkflow: (workflowId: string) =>
    apiClient.post(`${WORKFLOW_BASE}/workflows/${workflowId}/clone/`),

  validateWorkflow: (payload: Record<string, unknown>) =>
    apiClient.post(`${WORKFLOW_BASE}/workflows/validate/`, payload),

  fetchBindings: (params?: Record<string, unknown>) =>
    apiClient.get(
      `${WORKFLOW_BASE}/workflow-bindings/`,
      params ? { params } : undefined
    ),

  createBinding: (payload: Record<string, unknown>) =>
    apiClient.post(`${WORKFLOW_BASE}/workflow-bindings/`, payload),

  deleteBinding: (bindingId: string) =>
    apiClient.delete(`${WORKFLOW_BASE}/workflow-bindings/${bindingId}/`),

  fetchTriggers: (params?: Record<string, unknown>) =>
    apiClient.get(
      `${WORKFLOW_BASE}/workflow-triggers/`,
      params ? { params } : undefined
    ),

  enrollTargets: (workflowId: string, payload: Record<string, unknown>) =>
    apiClient.post(`${WORKFLOW_BASE}/workflows/${workflowId}/enroll/`, payload),

  fetchEnrollments: (workflowId: string, params?: Record<string, unknown>) =>
    apiClient.get(
      `${WORKFLOW_BASE}/workflows/${workflowId}/enrollments/`,
      params ? { params } : undefined
    ),

  unenrollTargets: (workflowId: string, payload: Record<string, unknown>) =>
    apiClient.post(
      `${WORKFLOW_BASE}/workflows/${workflowId}/unenroll/`,
      payload
    ),

  fetchRuns: (workflowId: string, params?: Record<string, unknown>) =>
    apiClient.get(
      `${WORKFLOW_BASE}/workflows/${workflowId}/runs/`,
      params ? { params } : undefined
    ),

  fetchRun: (runId: string, workspaceId?: string | null) =>
    apiClient.get(`${WORKFLOW_BASE}/workflow-runs/${runId}/`, {
      params: withWorkspace(workspaceId)
    }),

  createRuns: (workflowId: string, payload: Record<string, unknown>) =>
    apiClient.post(`${WORKFLOW_BASE}/workflows/${workflowId}/runs/`, payload),

  updateRunStatus: (
    runId: string,
    action: string,
    payload?: Record<string, unknown>,
    workspaceId?: string | null
  ) =>
    apiClient.post(
      `${WORKFLOW_BASE}/workflow-runs/${runId}/${action}/`,
      payload,
      { params: withWorkspace(workspaceId) }
    ),

  fetchRunEvents: (
    runId: string,
    params?: Record<string, unknown>,
    workspaceId?: string | null
  ) =>
    apiClient.get(`${WORKFLOW_BASE}/workflow-runs/${runId}/events/`, {
      params: withWorkspace(workspaceId, params)
    }),

  completeStep: (
    runId: string,
    nodeId: string,
    payload: Record<string, unknown>,
    workspaceId?: string | null
  ) =>
    apiClient.post(
      `${WORKFLOW_BASE}/workflow-runs/${runId}/steps/${nodeId}/complete/`,
      payload,
      { params: withWorkspace(workspaceId) }
    ),

  submitStepInput: (
    runId: string,
    nodeId: string,
    payload: Record<string, unknown>,
    workspaceId?: string | null
  ) =>
    apiClient.post(
      `${WORKFLOW_BASE}/workflow-runs/${runId}/steps/${nodeId}/input/`,
      payload,
      { params: withWorkspace(workspaceId) }
    ),

  fetchSchedules: (workflowId: string) =>
    apiClient.get(`${WORKFLOW_BASE}/workflows/${workflowId}/schedules/`),

  createSchedule: (workflowId: string, payload: Record<string, unknown>) =>
    apiClient.post(
      `${WORKFLOW_BASE}/workflows/${workflowId}/schedules/`,
      payload
    ),

  updateSchedule: (
    workflowId: string,
    scheduleId: string,
    payload: Record<string, unknown>
  ) =>
    apiClient.patch(
      `${WORKFLOW_BASE}/workflows/${workflowId}/schedules/${scheduleId}/`,
      payload
    ),

  deleteSchedule: (workflowId: string, scheduleId: string) =>
    apiClient.delete(
      `${WORKFLOW_BASE}/workflows/${workflowId}/schedules/${scheduleId}/`
    )
};
